/* eslint-disable react/no-array-index-key */
import React, { type FunctionComponent, type ReactNode } from 'react';
import type {
    ProductCardSizeType,
    ProductListItemModel,
    ProductRecommendationListModel,
    ProductRecommendationModel
} from 'autogen/swagger/all';
import styled, { ThemeProvider } from 'styled-components';
import useThemeHandler from 'components/productRecommendation/themes/useThemeHandler';
import Layout from 'components/Layout';
import { Spacing32, Spacing64 } from 'autogen/design-tokens/tokens';
import { breakpoint, sizes } from 'variables';
import LargeProductRecommendation from './components/LargeProductRecommendation';
import MediumProductRecommendation from './components/MediumProductRecommendation';
import SmallProductRecommendation from './components/SmallProductRecommendation';
import { filterProductRecommendationItems } from './shared/helpers';
import TrackingContext from 'modules/tracking/GA4/helpers/TrackingContext';
import useAlgonomyBrowser from './shared/hooks/useAlgonomyBrowser';

const ProductRecommendationContainer = styled.div`
    display: grid;
    grid-row-gap: ${Spacing32};
    padding: ${Spacing32} 0;

    ${breakpoint.up(sizes.lg)} {
        padding: ${Spacing64} 0;
        grid-row-gap: ${Spacing64};
    }
`;

const defaultRecommendationLayoutRenderer = (
    type: ProductCardSizeType
): FunctionComponent<{ items: ProductListItemModel[] }> => {
    switch (type) {
        case 'Small':
            return SmallProductRecommendation;
        case 'Medium':
            return MediumProductRecommendation;
        case 'Large':
            return LargeProductRecommendation;
        default:
            return MediumProductRecommendation;
    }
};

export type ProductRecommendation = {
    model: ProductRecommendationListModel;
    recommendationLayoutRenderer?: (type: ProductCardSizeType) => FunctionComponent<{ items: ProductListItemModel[] }>;
    recommendationRenderer?: (
        recommendationModel: ProductRecommendationModel,
        children: ReactNode,
        index
    ) => JSX.Element;
};

export default function ProductRecommendation({
    model,
    recommendationLayoutRenderer = defaultRecommendationLayoutRenderer,
    recommendationRenderer = (recommendationModel, children, index) => (
        /* We need to add the removePadding prop because the cms recommendation placeholder has built in container fluid wrappers. */
        <Layout.Section key={index} heading={recommendationModel.headline} removePadding>
            {children}
        </Layout.Section>
    ),
    ...restProps
}: ProductRecommendation) {
    const theme = useThemeHandler();
    useAlgonomyBrowser(model.enableExperienceBrowser);

    const { recommendationModels, uaTrackingCategory } = model;

    if (!recommendationModels.length) return null;

    return (
        <ThemeProvider theme={theme}>
            <ProductRecommendationContainer {...restProps}>
                {recommendationModels.map((recommendationModel, index) => {
                    const productListItems = filterProductRecommendationItems(recommendationModel, uaTrackingCategory); // backend should have filtered the recommendationModel to only contain ProductListItemModel. (we dont want connected content in our recommendation grid)
                    const ProductRecommendationLayout = recommendationLayoutRenderer(recommendationModel.placementType);

                    return recommendationRenderer(
                        recommendationModel,
                        <TrackingContext
                            eventParams={[
                                { param_name: 'recommendation_name', param_value: recommendationModel.headline }
                            ]}
                        >
                            <ProductRecommendationLayout items={productListItems} />
                        </TrackingContext>,
                        index
                    );
                })}
            </ProductRecommendationContainer>
        </ThemeProvider>
    );
}
